import { api } from '../client';

/**
 * Every call the web app makes, named and typed in one place.
 *
 * Pages ask for `sales.list()` rather than building URLs, so a route that
 * changes shape is fixed here once instead of in whichever screens happened to
 * call it.
 */

export interface Page<T> {
  data: T[];
  page?: { total: number; limit: number; offset: number; hasMore: boolean };
}

const qs = (params: Record<string, string | undefined>): string => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) search.set(k, v);
  const s = search.toString();
  return s ? `?${s}` : '';
};

export const endpoints = {
  auth: {
    login: (tenantCode: string, username: string, password: string) =>
      api.post('/auth/login', { tenantCode, username, password }),
    me: () => api.get('/auth/me'),
    logout: (refreshToken: string) => api.post('/auth/logout', { refreshToken }),
  },

  organisation: {
    branches: () => api.get<Page<{ id: string; code: string; name: string }>>('/branches'),
    companies: () => api.get<Page<Record<string, unknown>>>('/companies'),
    company: () => api.get<{ data: Record<string, unknown> | null }>('/company'),
    updateCompany: (body: Record<string, unknown>) => api.patch('/company', body),
  },

  masters: {
    metals: () => api.get<Page<{ id: string; code: string; name: string }>>('/metals'),
    purities: () => api.get<Page<Record<string, string>>>('/purities?limit=50'),
    products: (q?: string) => api.get<Page<Record<string, unknown>>>(`/products${qs({ q, limit: '50' })}`),
    taxGroups: () => api.get<Page<{ id: string; code: string }>>('/tax-groups?limit=200'),
    pricingRules: () => api.get<Page<{ id: string; code: string }>>('/pricing-rules?limit=200'),
  },

  parties: {
    customers: (q?: string) => api.get<Page<Record<string, unknown>>>(`/customers${qs({ q })}`),
    createCustomer: (body: Record<string, unknown>) => api.post('/customers', body),
    suppliers: (q?: string) => api.get<Page<Record<string, unknown>>>(`/suppliers${qs({ q })}`),
    customerLedger: (id: string) =>
      api.get<{ data: Record<string, string>[]; balance: string }>(`/parties/CUSTOMER/${id}/ledger`),
  },

  pricing: {
    rates: () => api.get<Page<Record<string, string>>>('/rates'),
    currentRate: (metalId: string, purityId?: string) =>
      api.get<Record<string, string>>(`/rates/current${qs({ metalId, purityId })}`),
    quote: (body: Record<string, unknown>) => api.post<Record<string, never>>('/pricing/quote', body),
  },

  inventory: {
    scan: (barcode: string, branchId?: string) =>
      api.get<Record<string, string>>(`/items/scan/${encodeURIComponent(barcode)}${qs({ branchId })}`),
    items: (params: { branchId?: string; status?: string; q?: string; limit?: string; locationId?: string }) =>
      api.get<Page<Record<string, unknown>>>(`/items${qs(params)}`),
    position: () => api.get<Page<Record<string, string>>>('/stock/position'),
    whereabouts: () =>
      api.get<{
        rows: { metalId: string; metalCode: string; metalName: string; location: string; fineWt: string }[];
        metals: { metalId: string; metalCode: string; metalName: string; total: string; byLocation: Record<string, string> }[];
      }>('/stock/whereabouts'),
  },

  sales: {
    list: (params: { from?: string; to?: string; branchId?: string; status?: string }) =>
      api.get<Page<Record<string, string>>>(`/sales${qs(params)}`),
    get: (id: string) => api.get<Record<string, never>>(`/sales/${id}`),
    post: (body: Record<string, unknown>, idempotencyKey: string) =>
      api.post<Record<string, string>>('/sales', body, { 'idempotency-key': idempotencyKey }),
    cancel: (id: string, reason: string) => api.post(`/sales/${id}/cancel`, { reason }),
  },

  workflow: {
    approvals: () => api.get<Page<Record<string, string>>>('/approvals'),
    decide: (id: string, decision: 'APPROVED' | 'REJECTED', reason?: string) =>
      api.post(`/approvals/${id}/decide`, { decision, ...(reason ? { reason } : {}) }),
    notifications: (unreadOnly = false) =>
      api.get<Page<Record<string, string>>>(`/notifications${qs({ unread: unreadOnly ? 'true' : undefined })}`),
    markRead: (id: string) => api.post(`/notifications/${id}/read`),
    markAllRead: () => api.post<{ marked: number }>('/notifications/read-all'),
  },

  reporting: {
    catalogue: () => api.get<Page<Record<string, never>>>('/reports'),
    run: (slug: string, params: Record<string, string | undefined>) =>
      api.get<Record<string, never>>(`/reports/${slug}/run${qs(params)}`),
    exportCsv: (slug: string, params: Record<string, string | undefined>) =>
      api.download(`/v1/reports/${slug}/export${qs(params)}`, `${slug}.csv`),
    dashboard: (asOf?: string) => api.get<Record<string, never>>(`/dashboard/summary${qs({ asOf })}`),
    chart: (key: string, params: { from?: string; to?: string } = {}) =>
      api.get<Record<string, never>>(`/dashboard/charts/${key}${qs(params)}`),
  },
};
